import React from 'react';

/**
 * Print-friendly global styles injected via a <style> tag.
 * When the user prints (Cmd+P) or exports to PDF, these styles:
 * - Hide navigation, toolbars, edit controls
 * - Force white background
 * - Expand charts to full width
 * - Add page-break hints
 * - Show a print header with timestamp
 */
export function PrintStyles() {
  return (
    <style>{`
      @media print {
        /* Hide non-essential UI */
        nav,
        header,
        aside,
        .no-print,
        [data-no-print],
        .command-palette,
        .toast-container,
        button:not(.print-keep),
        .edit-toolbar,
        [role="dialog"],
        [role="alertdialog"] {
          display: none !important;
        }

        /* Reset dark mode */
        html, body {
          background: white !important;
          color: #111827 !important;
          font-size: 12px !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        .dark\\:bg-gray-800,
        .dark\\:bg-gray-900,
        [class*="dark:bg-"] {
          background: white !important;
        }

        .dark\\:text-white,
        .dark\\:text-gray-100,
        .dark\\:text-gray-200,
        .dark\\:text-gray-300,
        [class*="dark:text-"] {
          color: #111827 !important;
        }

        /* Optimize layout — flex grid for print */
        .dashboard-grid,
        .widget-grid {
          display: block !important;
        }

        .react-grid-layout {
          height: auto !important;
          display: flex !important;
          flex-wrap: wrap !important;
          gap: 8px !important;
        }

        .react-grid-item {
          position: relative !important;
          transform: none !important;
          margin-bottom: 8px;
          page-break-inside: avoid;
        }

        /* KPI cards: 4 per row in print */
        .react-grid-item[data-widget-type^="kpi_"] {
          width: calc(25% - 8px) !important;
          flex: 0 0 calc(25% - 8px) !important;
        }

        /* Chart/list widgets: 2 per row in print */
        .react-grid-item:not([data-widget-type^="kpi_"]) {
          width: calc(50% - 6px) !important;
          flex: 0 0 calc(50% - 6px) !important;
        }

        /* Advanced analytics sections: 2 per row */
        .print-analytics-grid {
          display: flex !important;
          flex-wrap: wrap !important;
          gap: 8px !important;
        }

        .print-analytics-grid > div {
          width: calc(50% - 4px) !important;
          flex: 0 0 calc(50% - 4px) !important;
          page-break-inside: avoid;
        }

        /* Charts */
        .recharts-responsive-container {
          width: 100% !important;
          min-height: 180px;
        }

        /* Cards */
        .rounded-xl {
          border: 1px solid #e5e7eb !important;
          box-shadow: none !important;
        }

        /* Page breaks */
        h1, h2, h3 {
          page-break-after: avoid;
        }

        .page-break-before {
          page-break-before: always;
        }

        /* Hide live/realtime indicators in print */
        [data-realtime-indicator],
        .realtime-indicator {
          display: none !important;
        }

        /* Print header */
        .print-header {
          display: block !important;
        }

        /* Show sr-only data tables in print */
        .sr-only {
          position: static !important;
          width: auto !important;
          height: auto !important;
          overflow: visible !important;
          clip: auto !important;
          white-space: normal !important;
        }

        /* Page setup */
        @page {
          margin: 1cm;
          size: A4 landscape;
        }
      }

      /* Always hidden in screen, shown in print */
      .print-header {
        display: none;
      }

      @media print {
        .print-header {
          display: flex !important;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 12px;
          margin-bottom: 16px;
          border-bottom: 2px solid #e5e7eb;
        }
      }
    `}</style>
  );
}

/**
 * A print header that appears only when printing. Place at the top of the dashboard.
 */
export function PrintHeader({ title }: { title: string }) {
  return (
    <div className="print-header">
      <div>
        <div style={{ fontSize: '18px', fontWeight: 700 }}>{title}</div>
        <div style={{ fontSize: '11px', color: '#6b7280' }}>
          CentraOnePlus Analytics
        </div>
      </div>
      <div style={{ textAlign: 'right', fontSize: '11px', color: '#6b7280' }}>
        <div>Printed: {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}</div>
        <div>Generated by CentraOnePlus</div>
      </div>
    </div>
  );
}

/**
 * Print button that triggers window.print()
 */
export function PrintButton({
  className = '',
  controlClassName,
}: {
  className?: string;
  /** Replaces the default look entirely (e.g. a page header's control style). */
  controlClassName?: string;
}) {
  return (
    <button
      type="button"
      onClick={() => window.print()}
      className={
        controlClassName
          ? `${controlClassName} no-print ${className}`
          : `flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 no-print ${className}`
      }
      title="Print or export to PDF (Cmd+P)"
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0 1 10.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 0 1-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0 0 21 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 0 0-1.913-.247M6.34 18H5.25A2.25 2.25 0 0 1 3 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 0 1 1.913-.247m0 0a48.09 48.09 0 0 1 12.5 0m-12.5 0V5.625c0-.621.504-1.125 1.125-1.125h9.75c.621 0 1.125.504 1.125 1.125v3.034" />
      </svg>
      <span className="hidden sm:inline">Print</span>
    </button>
  );
}
